import { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  format,
  startOfYear,
  endOfYear,
  eachWeekOfInterval,
  eachDayOfInterval,
  endOfWeek,
  isSameDay,
  getMonth,
} from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { sessionsByProjectGroupSelector } from "../../../stats/state/statsSlice";
import { getEnduserAllSessions } from "../../../enduserStats/state/enduserStatsSlice";
import { getSessionDate } from "../../utils/dateUtils";

interface ActivityHeatmapProps {
  year?: number;
  maxCellSize?: number;
  showYearNavigation?: boolean;
  /**
   * Variant determines which data source to use:
   * - "creator": Uses sessionsByProjectGroupSelector (all org sessions)
   * - "enduser": Uses getEnduserAllSessions (only current user's sessions)
   */
  variant?: "creator" | "enduser";
}

const ActivityHeatmap = ({
  year: initialYear,
  maxCellSize = 16,
  showYearNavigation = true,
  variant = "creator",
}: ActivityHeatmapProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "fr" ? fr : enUS;

  // Select data based on variant
  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);
  const enduserAllSessions = useSelector(getEnduserAllSessions);

  const containerRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState(10);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(initialYear || currentYear);
  const year = initialYear || selectedYear;

  // Year navigation handlers
  const goToPrevYear = () => setSelectedYear((prev) => prev - 1);
  const goToNextYear = () => setSelectedYear((prev) => Math.min(prev + 1, currentYear));
  const canGoNext = selectedYear < currentYear;

  // Calculate cell size based on container width
  useEffect(() => {
    const updateCellSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // 53 weeks + day labels (28px) + gaps (2px * 52)
        const dayLabelsWidth = 28;
        const numWeeks = 53;
        const gapSize = 2;
        const totalGaps = (numWeeks - 1) * gapSize;
        const availableWidth = containerWidth - dayLabelsWidth - totalGaps - 16; // 16px padding
        const newCellSize = Math.floor(availableWidth / numWeeks);
        // Clamp between 10 and maxCellSize
        setCellSize(Math.max(10, Math.min(maxCellSize, newCellSize)));
      }
    };

    updateCellSize();
    window.addEventListener("resize", updateCellSize);
    return () => window.removeEventListener("resize", updateCellSize);
  }, [maxCellSize]);

  // Calculate session counts per day based on variant
  const sessionCountsByDay = useMemo(() => {
    const counts = new Map<string, number>();

    if (variant === "enduser") {
      // Enduser variant: flat array of sessions
      if (enduserAllSessions) {
        enduserAllSessions.forEach((session) => {
          if (session.createdAt) {
            const sessionDate = new Date(session.createdAt);
            const dateKey = format(sessionDate, "yyyy-MM-dd");
            counts.set(dateKey, (counts.get(dateKey) || 0) + 1);
          }
        });
      }
    } else {
      // Creator variant: nested groups/projects/sessions
      sessionsByProjectGroup.forEach((group) => {
        group.projects.forEach((project) => {
          project.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);
            const dateKey = format(sessionDate, "yyyy-MM-dd");
            counts.set(dateKey, (counts.get(dateKey) || 0) + 1);
          });
        });
      });
    }

    return counts;
  }, [variant, sessionsByProjectGroup, enduserAllSessions]);

  // Get max value for color scaling
  const maxCount = useMemo(() => {
    let max = 0;
    sessionCountsByDay.forEach((count) => {
      if (count > max) max = count;
    });
    return max || 1;
  }, [sessionCountsByDay]);

  // Generate weeks for the year
  const weeksData = useMemo(() => {
    const yearStart = startOfYear(new Date(year, 0, 1));
    const yearEnd = endOfYear(new Date(year, 0, 1));

    // Get all weeks (starting Monday)
    const weeks = eachWeekOfInterval({ start: yearStart, end: yearEnd }, { weekStartsOn: 1 });

    return weeks.map((weekStart) => {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
      const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

      return days.map((day) => {
        // Check if day is in the current year
        if (day.getFullYear() !== year) {
          return { date: day, count: -1, inYear: false };
        }

        const dateKey = format(day, "yyyy-MM-dd");
        const count = sessionCountsByDay.get(dateKey) || 0;
        return { date: day, count, inYear: true };
      });
    });
  }, [year, sessionCountsByDay]);

  // Month labels with positions
  const monthLabels = useMemo(() => {
    const labels: { label: string; colStart: number }[] = [];
    let lastMonth = -1;

    weeksData.forEach((week, weekIndex) => {
      // Check the first day of the week that's in the year
      const firstDayInYear = week.find((d) => d.inYear);
      if (firstDayInYear) {
        const month = getMonth(firstDayInYear.date);
        if (month !== lastMonth) {
          lastMonth = month;
          labels.push({
            label: format(firstDayInYear.date, "MMM", { locale }),
            colStart: weekIndex,
          });
        }
      }
    });

    return labels;
  }, [weeksData, locale]);

  // Color based on intensity
  const getColor = (count: number, inYear: boolean): string => {
    if (!inYear) return "bg-transparent";
    if (count === 0) return "bg-white border border-gray-200";

    const intensity = count / maxCount;
    if (intensity <= 0.25) return "bg-green-200";
    if (intensity <= 0.5) return "bg-green-300";
    if (intensity <= 0.75) return "bg-green-500";
    return "bg-green-600";
  };

  // Total sessions this year
  const totalSessionsThisYear = useMemo(() => {
    let total = 0;
    const yearStart = startOfYear(new Date(year, 0, 1));
    const yearEnd = endOfYear(new Date(year, 0, 1));

    sessionCountsByDay.forEach((count, dateKey) => {
      const date = new Date(dateKey);
      if (date >= yearStart && date <= yearEnd) {
        total += count;
      }
    });

    return total;
  }, [sessionCountsByDay, year]);

  // Day labels (Monday = index 0 because weekStartsOn: 1)
  const dayLabels =
    i18n.language === "fr"
      ? ["Lun", "", "Mer", "", "Ven", "", ""]
      : ["Mon", "", "Wed", "", "Fri", "", ""];

  return (
    <div ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">{t("statsNew.heatmap.title")}</h3>
          <span className="text-xs text-gray-500">
            {totalSessionsThisYear.toLocaleString()} {t("statsNew.heatmap.sessionsIn")} {year}
          </span>
        </div>

        {/* Year Navigation */}
        {showYearNavigation && !initialYear && (
          <div className="flex items-center gap-2">
            <button
              onClick={goToPrevYear}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title={t("statsNew.charts.prevYear")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="text-sm font-semibold text-gray-900 min-w-[50px] text-center">
              {selectedYear}
            </span>
            <button
              onClick={goToNextYear}
              disabled={!canGoNext}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title={t("statsNew.charts.nextYear")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Heatmap container */}
      <div className="overflow-x-auto pb-2">
        <div className="inline-flex flex-col min-w-max">
          {/* Month labels */}
          <div className="ml-7 mb-1 h-4 relative">
            {monthLabels.map(({ label, colStart }, idx) => (
              <span
                key={idx}
                className="text-xs text-gray-500 absolute whitespace-nowrap"
                style={{ left: `${colStart * (cellSize + 2)}px` }}
              >
                {label}
              </span>
            ))}
          </div>

          {/* Grid with day labels */}
          <div className="flex">
            {/* Day labels column */}
            <div className="flex flex-col gap-[2px] mr-1 text-[10px] text-gray-500">
              {dayLabels.map((label, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-end pr-1"
                  style={{ width: "24px", height: `${cellSize}px` }}
                >
                  {label}
                </div>
              ))}
            </div>

            {/* Weeks grid */}
            <div className="flex gap-[2px]">
              {weeksData.map((week, weekIndex) => (
                <div key={weekIndex} className="flex flex-col gap-[2px]">
                  {week.map((day, dayIndex) => {
                    const isToday = day.inYear && isSameDay(day.date, new Date());
                    const sessionLabel =
                      day.count === 0
                        ? t("statsNew.heatmap.noSession")
                        : day.count === 1
                        ? t("statsNew.heatmap.oneSession")
                        : t("statsNew.heatmap.sessions", { count: day.count });
                    const tooltipText = day.inYear
                      ? `${sessionLabel} - ${format(day.date, "EEEE d MMMM yyyy", { locale })}`
                      : "";

                    return (
                      <div
                        key={dayIndex}
                        className={`rounded-sm ${getColor(day.count, day.inYear)} ${
                          isToday ? "ring-1 ring-gray-400 ring-offset-1" : ""
                        } transition-colors hover:ring-1 hover:ring-gray-300 cursor-default`}
                        style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
                        title={tooltipText}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-end gap-2 mt-1 text-xs text-gray-500">
            <span>{t("statsNew.heatmap.less")}</span>
            <div className="flex gap-[2px]">
              <div
                className="rounded-sm bg-white border border-gray-200"
                style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
                title="0"
              />
              <div
                className="rounded-sm bg-green-200"
                style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
              />
              <div
                className="rounded-sm bg-green-300"
                style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
              />
              <div
                className="rounded-sm bg-green-500"
                style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
              />
              <div
                className="rounded-sm bg-green-600"
                style={{ width: `${cellSize}px`, height: `${cellSize}px` }}
              />
            </div>
            <span>{t("statsNew.heatmap.more")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityHeatmap;

/**
 * @deprecated Use ActivityHeatmap with variant="enduser" instead
 * Kept for backward compatibility
 */
export const EnduserActivityHeatmap = (props: Omit<ActivityHeatmapProps, "variant">) => (
  <ActivityHeatmap {...props} variant="enduser" />
);
